/** Lead intake types (CRM-129) — public capture form and the review queue. */

export type LeadSubmissionStatus = 'PENDING_REVIEW' | 'APPROVED' | 'DISCARDED';

/** Queue filters (CRM-134). */
export interface LeadQueueFilters {
  sourceId?: string;
  /** Current assignee of the person this lead resolved to; follows later reassignment. */
  assignedTo?: string;
  dateFrom?: string; // YYYY-MM-DD (Pacific calendar day)
  dateTo?: string; // YYYY-MM-DD (Pacific calendar day)
}

export type LeadDiscardReason = 'spam' | 'athlete' | 'duplicate' | 'other';

export const HEARD_ABOUT_US_OPTIONS: { value: string; label: string }[] = [
  { value: 'referral', label: 'Referral' },
  { value: 'internet_search', label: 'Internet search' },
  { value: 'ad', label: 'Advertisement' },
  { value: 'email', label: 'Email' },
  { value: 'sales_rep', label: 'Blast sales rep' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'other', label: 'Other' },
];

/**
 * Mirrors `PRIMARY_INTEREST_LABELS` in the backend validator, which owns the
 * vocabulary because the approve flow writes these labels into the rep's Task.
 */
export const PRIMARY_INTEREST_OPTIONS: { value: string; label: string }[] = [
  { value: 'fundraising', label: 'Fundraising' },
  { value: 'media_guide', label: 'Media Guide / Digital Gameday Program' },
  { value: 'team_stores', label: 'Team Stores' },
  { value: 'uniforms_apparel', label: 'Uniforms & Apparel' },
  { value: 'all_of_the_above', label: 'All of the above' },
];

/**
 * Roles the form offers, matching the CRM's own person-form suggestions so an
 * approved lead lands with a title the directory already uses. "Other" reveals a
 * free-text box and is never stored as-is.
 */
export const TITLE_OPTIONS = [
  'Head Coach',
  'Assistant Coach',
  'Athletic Director',
  'Associate AD',
  'Coordinator',
  'Booster Club President',
];

export const TITLE_OTHER = 'Other';

/** Post-submit note cap; the backend rejects anything longer. */
export const MAX_LEAD_NOTE_LENGTH = 500;

/** Only label + code ever leave the public endpoint. */
export interface PublicLeadSource {
  label: string;
  code: string;
}

export interface LeadSource {
  id: string;
  label: string;
  /** Derived from the label at creation and immutable afterwards — printed on collateral. */
  code: string;
  isActive: boolean;
  createdAt: string;
  createdBy: { id: string; displayName: string | null; email: string } | null;
}

export interface LeadSubmissionListItem {
  id: string;
  firstName: string;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  orgName: string | null;
  sportName: string | null;
  title: string | null;
  heardAboutUs: string | null;
  primaryInterest: string | null;
  /** Left by the coach on the thanks screen after submitting. */
  note: string | null;
  sourceCodeRaw: string | null;
  /** The website page the submission came from (the widget sends its path). */
  sourcePath: string | null;
  status: LeadSubmissionStatus;
  discardReason: string | null;
  decidedAt: string | null;
  resolvedPersonId: string | null;
  createdAt: string;
  source: { id: string; label: string; code: string } | null;
  repUser: { id: string; displayName: string | null; email: string } | null;
  decidedBy: { id: string; displayName: string | null; email: string } | null;
  /** Existing coach with the same email — shown before deciding so a link is never a surprise. */
  duplicatePerson: {
    id: string;
    firstName: string;
    lastName: string;
    isActive: boolean;
    assignedUser: { id: string; displayName: string | null; email: string } | null;
  } | null;
}

export interface LeadApproveResult {
  submissionId: string;
  personId: string;
  outcome: 'CREATED' | 'LINKED' | 'REACTIVATED';
  assignedUserId: string | null;
  taskId: string | null;
}

export interface PublicLeadFormValues {
  /** Marks the v1.1 required-field contract; the backend still accepts payloads without it. */
  formVersion: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  zip: string;
  orgName: string;
  sportName: string;
  title: string;
  heardAboutUs: string;
  primaryInterest: string;
  sourceCode: string;
  honeypot: string;
}
